import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { LogOut, Download, Mail, Phone, FileText, Inbox, Loader2, RefreshCw, KeyRound, Box, Tag, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import ChangePasswordModal from "@/components/ChangePasswordModal";
import STLPreviewModal from "@/components/STLPreviewModal";
import PricingModal from "@/components/PricingModal";
import { LOGO_URL } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = ["nuovo", "in lavorazione", "completato", "archiviato"];
const STATUS_STYLE = {
  nuovo: "bg-[#00C8D7]/15 text-[#00C8D7] border-[#00C8D7]/40",
  "in lavorazione": "bg-[#F5871F]/15 text-[#F5871F] border-[#F5871F]/40",
  completato: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40",
  archiviato: "bg-slate-500/15 text-slate-400 border-slate-500/40",
};

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

const fmtSize = (b) => (b ? `${(b / 1024).toFixed(0)} KB` : "—");

export default function Admin() {
  const { user, token, logout } = useAuth();
  const nav = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPwd, setShowPwd] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [preview, setPreview] = useState(null);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const stats = {
    total: quotes.length,
    nuovi: quotes.filter((q) => q.status === "nuovo").length,
    with_file: quotes.filter((q) => q.stored_file || q.file_name).length,
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/quotes`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("cp3d_token")}` },
      });
      setQuotes(data);
    } catch {
      toast.error("Impossibile caricare le richieste.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user === false) nav("/admin/login");
    if (user && user.email) load();
  }, [user, nav, load]);

  const changeStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/quotes/${id}`, { status }, auth);
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
      toast.success("Stato aggiornato.");
    } catch {
      toast.error("Aggiornamento non riuscito.");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/quotes/export`, { ...auth, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `preventivi_copypro3d_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export CSV scaricato.");
    } catch {
      toast.error("Export non riuscito.");
    }
  };

  const downloadFile = async (q) => {
    try {
      const res = await axios.get(`${API}/quotes/${q.id}/file`, { ...auth, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = q.file_name || "file";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Download non riuscito.");
    }
  };

  if (user === null) {
    return (
      <div className="min-h-screen bg-[#0A0E14] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#00C8D7]" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0E14] text-white">
      {/* Header */}
      <header className="border-b border-white/10 bg-[#0A0E14]/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="copypro3d.it" className="h-10 w-10 rounded-full object-cover" />
            <div>
              <h1 className="font-display font-bold text-lg tracking-tight">Area Richieste</h1>
              <span className="font-mono-tech text-xs text-slate-500">{user.email}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh"
              onClick={load}
              className="p-2.5 border border-white/15 text-slate-300 hover:text-white hover:border-white/40 transition-colors"
            >
              <RefreshCw size={18} />
            </button>
            <button
              data-testid="admin-export-csv"
              onClick={exportCsv}
              className="hidden sm:inline-flex items-center gap-2 border border-white/15 text-slate-300 hover:text-white hover:border-white/40 px-4 py-2.5 text-sm transition-colors"
            >
              <FileSpreadsheet size={16} /> Esporta CSV
            </button>
            <button
              data-testid="admin-pricing"
              onClick={() => setShowPricing(true)}
              className="hidden sm:inline-flex items-center gap-2 border border-white/15 text-slate-300 hover:text-white hover:border-white/40 px-4 py-2.5 text-sm transition-colors"
            >
              <Tag size={16} /> Listino
            </button>
            <button
              data-testid="admin-change-password"
              onClick={() => setShowPwd(true)}
              className="hidden sm:inline-flex items-center gap-2 border border-white/15 text-slate-300 hover:text-white hover:border-white/40 px-4 py-2.5 text-sm transition-colors"
            >
              <KeyRound size={16} /> Cambia password
            </button>
            <button
              data-testid="admin-logout"
              onClick={() => { logout(); nav("/admin/login"); }}
              className="inline-flex items-center gap-2 border border-white/15 text-slate-300 hover:text-white hover:border-white/40 px-4 py-2.5 text-sm transition-colors"
            >
              <LogOut size={16} /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
          {[
            { l: "Richieste totali", v: stats.total, icon: Inbox },
            { l: "Nuove", v: stats.nuovi, icon: Mail },
            { l: "Con file allegato", v: stats.with_file, icon: FileText },
          ].map((s, i) => (
            <div key={i} data-testid={`stat-${i}`} className="bg-[#111827] border border-white/10 p-6">
              <div className="flex items-center justify-between">
                <span className="font-mono-tech text-xs uppercase tracking-[0.2em] text-slate-500">{s.l}</span>
                <s.icon size={18} className="text-[#00C8D7]" />
              </div>
              <p className="font-display font-black text-4xl mt-3">{s.v}</p>
            </div>
          ))}
        </div>

        <h2 className="font-display font-bold text-2xl tracking-tight mb-6">Preventivi ricevuti</h2>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="animate-spin text-[#00C8D7]" size={28} />
          </div>
        ) : quotes.length === 0 ? (
          <div data-testid="admin-empty" className="text-center py-20 border border-dashed border-white/15">
            <Inbox size={40} className="text-slate-600 mx-auto" />
            <p className="text-slate-400 mt-4">Nessuna richiesta ricevuta per ora.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-quotes-list">
            {quotes.map((q, i) => (
              <motion.div
                key={q.id}
                data-testid={`admin-quote-${i}`}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.03 }}
                className="bg-[#111827] border border-white/10 p-6 hover:border-white/20 transition-colors"
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-display font-bold text-xl tracking-tight">{q.name}</h3>
                      <span className={`font-mono-tech text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 border ${STATUS_STYLE[q.status] || STATUS_STYLE.nuovo}`}>
                        {q.status}
                      </span>
                      {q.project_type && (
                        <span className="font-mono-tech text-xs text-slate-500">{q.project_type}</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-4 mt-3 text-sm text-slate-400">
                      <a href={`mailto:${q.email}`} className="flex items-center gap-1.5 hover:text-[#00C8D7]">
                        <Mail size={14} /> {q.email}
                      </a>
                      {q.phone && (
                        <a href={`tel:${q.phone}`} className="flex items-center gap-1.5 hover:text-[#00C8D7]">
                          <Phone size={14} /> {q.phone}
                        </a>
                      )}
                      <span className="font-mono-tech text-xs text-slate-600">{fmtDate(q.created_at)}</span>
                    </div>
                    <p className="mt-4 text-slate-300 leading-relaxed whitespace-pre-wrap">{q.message}</p>
                    {(q.dimensions || q.estimated_price || q.material) && (
                      <div data-testid={`admin-estimate-${i}`} className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-white/10 pt-3 font-mono-tech text-xs text-slate-400">
                        {q.dimensions && <span>DIM: <span className="text-slate-200">{q.dimensions}</span></span>}
                        {q.material && <span>MAT: <span className="text-slate-200">{q.material}</span></span>}
                        {q.quality && <span>QUALITÀ: <span className="text-slate-200">{q.quality}</span></span>}
                        {q.quantity && <span>QTÀ: <span className="text-slate-200">{q.quantity}</span></span>}
                        {q.estimated_price != null && (
                          <span className="text-[#00C8D7]">STIMA: ~{q.estimated_price}€</span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-3 md:items-end shrink-0">
                    {q.file_name ? (
                      <button
                        data-testid={`admin-download-${i}`}
                        onClick={() => downloadFile(q)}
                        className="inline-flex items-center gap-2 bg-[#00C8D7] text-[#0A0E14] font-bold text-sm px-4 py-2.5 hover:bg-[#00C8D7]/85 transition-colors"
                      >
                        <Download size={16} /> {q.file_name.length > 22 ? "Scarica file" : q.file_name}
                        <span className="font-mono-tech text-xs opacity-70">{fmtSize(q.file_size)}</span>
                      </button>
                    ) : (
                      <span className="font-mono-tech text-xs text-slate-600">Nessun file</span>
                    )}
                    {q.file_name && q.file_name.toLowerCase().endsWith(".stl") && (
                      <button
                        data-testid={`admin-preview-${i}`}
                        onClick={() => setPreview(q)}
                        className="inline-flex items-center gap-2 border border-[#00C8D7] text-[#00C8D7] font-bold text-sm px-4 py-2.5 hover:bg-[#00C8D7]/10 transition-colors"
                      >
                        <Box size={16} /> Anteprima 3D
                      </button>
                    )}
                    <select
                      data-testid={`admin-status-${i}`}
                      value={q.status}
                      onChange={(e) => changeStatus(q.id, e.target.value)}
                      className="bg-[#0A0E14] border border-white/15 text-sm px-3 py-2 text-slate-200 focus:outline-none focus:border-[#00C8D7]"
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      <ChangePasswordModal open={showPwd} onClose={() => setShowPwd(false)} token={token} />
      <PricingModal open={showPricing} onClose={() => setShowPricing(false)} token={token} />
      <STLPreviewModal quote={preview} token={token} onClose={() => setPreview(null)} />
    </div>
  );
}
